import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { MultiSelect, type MultiOption } from "@/components/ui/multi-select";
import { useToast } from "@/components/ui/toast";
import { UserSearchMore, useUserSearch } from "@/features/user-search";
import { ApiError } from "@/lib/api";
import { sameIds } from "@/lib/form";
import { withPicked } from "@/lib/paging";
import { useAddTeamMembers, useCreateTeam, useUpdateTeam } from "@/lib/queries";
import { personName } from "@/lib/teams";
import { atLeast, type AppUser, type Team } from "@/lib/types";

/**
 * Making a team, renaming it or changing its leads (platform ADR-207), opening its chat to everyone (platform ADR-190),
 * and adding people to it (platform ADR-185). Supervisors and admins do all of it, among the people
 * they can see; core-api says so when a pick is not theirs to make.
 */

const option = (u: Pick<AppUser, "id" | "display_name" | "email" | "sip_endpoint_extension">): MultiOption => ({
  value: String(u.id),
  label: personName(u),
  hint: u.sip_endpoint_extension ? `ext. ${u.sip_endpoint_extension} · ${u.email}` : `${u.email} · no phone`,
});

/** Picks a team's leads (platform ADR-207): supervisors and admins, searched for on the server. */
function LeadsPicker({
  value, onChange, current,
}: {
  value: number[];
  onChange: (ids: number[]) => void;
  /** The leads the team has now, named even when the matches do not hold them. */
  current: readonly MultiOption[];
}) {
  const [query, setQuery] = React.useState("");
  const people = useUserSearch(query, { is_active: true });
  const [named, setNamed] = React.useState<Map<number, MultiOption>>(new Map());
  const matches = people.users.filter((u) => atLeast(u.role, "supervisor")).map(option);
  const picked = value
    .map(
      (id) =>
        matches.find((o) => o.value === String(id)) ??
        named.get(id) ??
        current.find((o) => o.value === String(id)),
    )
    .filter((o): o is MultiOption => o !== undefined);
  return (
    <MultiSelect
      label="Leads"
      options={withPicked(picked, matches, people.searching, (o) => o.value)}
      value={value.map(String)}
      picked={picked}
      onChange={(ids) => {
        const next = new Map(named);
        for (const o of matches) next.set(Number(o.value), o);
        setNamed(next);
        onChange(ids.map(Number));
      }}
      onSearch={setQuery}
      searchedFor={people.searchedFor}
      loading={people.loading}
      footer={<UserSearchMore search={people} />}
      placeholder="Search supervisors and admins"
      noneText="No lead yet"
      emptyText="No supervisors or admins match"
    />
  );
}

/** Picks people to put in a team, searched for on the server. */
function PeoplePicker({
  value, onChange, exclude,
}: {
  value: number[];
  onChange: (ids: number[]) => void;
  /** Already in the team: not offered again. */
  exclude: ReadonlySet<number>;
}) {
  const [query, setQuery] = React.useState("");
  const people = useUserSearch(query, { is_active: true });
  const [named, setNamed] = React.useState<Map<number, MultiOption>>(new Map());
  const matches = people.users.filter((u) => !exclude.has(u.id)).map(option);
  const picked = value.map((id) => matches.find((o) => o.value === String(id)) ?? named.get(id)).filter(
    (o): o is MultiOption => o !== undefined,
  );
  return (
    <MultiSelect
      label="People"
      options={withPicked(picked, matches, people.searching, (o) => o.value)}
      value={value.map(String)}
      picked={picked}
      onChange={(ids) => {
        const next = new Map(named);
        for (const o of matches) next.set(Number(o.value), o);
        setNamed(next);
        onChange(ids.map(Number));
      }}
      onSearch={setQuery}
      searchedFor={people.searchedFor}
      loading={people.loading}
      footer={<UserSearchMore search={people} />}
      placeholder="Search by name or email"
      noneText="Nobody picked yet"
      emptyText="Nobody else to add"
    />
  );
}

/** Make a team, or rename one, change its leads and open or close its chat. */
export function TeamFormDialog({
  open, onOpenChange, team, onMade,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent to make a new one. */
  team?: Team;
  onMade?: (team: Team) => void;
}) {
  const toast = useToast();
  const create = useCreateTeam();
  const update = useUpdateTeam();
  const [name, setName] = React.useState("");
  const [leads, setLeads] = React.useState<number[]>([]);
  const [people, setPeople] = React.useState<number[]>([]);
  const [openChat, setOpenChat] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setName(team?.name ?? "");
    setLeads(team?.lead_user_ids ?? []);
    setPeople([]);
    setOpenChat(team?.open_chat ?? false);
  }, [open, team]);

  const changed =
    name !== (team?.name ?? "") ||
    !sameIds(leads, team?.lead_user_ids ?? []) ||
    people.length > 0 ||
    openChat !== (team?.open_chat ?? false);
  const currentLeads: MultiOption[] = (team?.leads ?? []).map((l) => ({
    value: String(l.user_id),
    label: personName(l),
    hint: l.email,
  }));

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError("Name the team.");
      return;
    }
    try {
      if (team) {
        await update.mutateAsync({ id: team.id, name: name.trim(), lead_user_ids: leads, open_chat: openChat });
        toast.success("Team saved", name.trim());
      } else {
        const made = await create.mutateAsync({
          name: name.trim(), lead_user_ids: leads, user_ids: people, open_chat: openChat,
        });
        toast.success("Team made", `${made.name}: ${made.members.length} people`);
        onMade?.(made);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the team.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{team ? "Edit the team" : "New team"}</DialogTitle>
          <DialogDescription>
            A team is a group of people with its leads. Put it on a campaign's line from the campaign's
            Staffing tab, and everyone in it with a phone takes that line's calls.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Name" htmlFor="team-name" required>
              <Input
                id="team-name"
                value={name}
                maxLength={80}
                placeholder="Closers"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field
              label="Leads"
              htmlFor="team-leads"
              hint="Supervisors or admins, as many as run the team. Each is in its chat, and on none of its lines unless they are in the team too."
            >
              <LeadsPicker value={leads} onChange={setLeads} current={currentLeads} />
            </Field>
            {!team && (
              <Field label="People" htmlFor="team-people" hint="You can add more later.">
                <PeoplePicker value={people} onChange={setPeople} exclude={new Set()} />
              </Field>
            )}
            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p id="team-open-chat-label" className="text-xs font-medium text-text">
                  Anyone can chat with this team
                </p>
                <p id="team-open-chat-hint" className="text-xs text-text-muted">
                  People outside the team find its chat under New message, read it and post in it. A setter who asks
                  it for a closer is put in it. They cannot take an ask, and its lines do not staff them. Turned
                  off, they are taken out; what they wrote stays.
                </p>
              </div>
              <Switch
                checked={openChat}
                onCheckedChange={setOpenChat}
                aria-labelledby="team-open-chat-label"
                aria-describedby="team-open-chat-hint"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={create.isPending || update.isPending}>
              {team ? "Save" : "Make the team"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/** Add people to a team. They go on every line it staffs. */
export function AddPeopleDialog({
  open, onOpenChange, team,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team: Team;
}) {
  const toast = useToast();
  const add = useAddTeamMembers();
  const [people, setPeople] = React.useState<number[]>([]);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setPeople([]);
  }, [open]);

  const inTeam = React.useMemo(() => new Set(team.members.map((m) => m.user_id)), [team.members]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (people.length === 0) {
      setFormError("Pick somebody to add.");
      return;
    }
    try {
      await add.mutateAsync({ teamId: team.id, userIds: people });
      toast.success(people.length === 1 ? "Person added" : `${people.length} people added`, team.name);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not add them.");
    }
  };

  const lines = team.lines.map((l) => l.queue_name);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={people.length > 0} />
        <DialogHeader>
          <DialogTitle>Add people to {team.name}</DialogTitle>
          <DialogDescription>
            {lines.length === 0
              ? "The team staffs no line yet, so nobody's calls change."
              : `Everyone added with a phone goes on ${lines.join(", ")} and is offered its calls at their next Ready. Nobody's skills change.`}
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="People" htmlFor="team-add-people" required>
              <PeoplePicker value={people} onChange={setPeople} exclude={inTeam} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={add.isPending}>
              Add
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
